import {
  ChangeDetectionStrategy,
  Component,
  Input,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import {
  combineLatest,
  debounceTime,
  filter,
  map,
  pipe,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { Customers } from 'server/shared/collections/customers';
import { Customer } from 'server/shared/models/customer';
import { RoleEnum } from 'server/shared/models/role.enum';
import { DispoUser, RequesterUser } from 'server/shared/models/user';
import { ToastService } from 'src/app/services/toast/toast.service';
import { UserService } from 'src/app/services/user.service';
import { rxFilterArrayIsNotEmpty } from 'src/helpers/filterIsNotEmpty';
import { formControlValue$ } from 'src/helpers/formControlValue$';
import { rxNgZoneRun } from 'src/helpers/rxNgZoneRun';
import { stringIncludes } from '../../../../helpers/stringIncludes';
import { SubscriptionService } from '../../subscription.service';

@Component({
  selector: 'app-customer-switcher',
  templateUrl: './customer-switcher.component.html',
  styleUrls: ['./customer-switcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerSwitcherComponent implements OnDestroy {
  @Input() loggedInUser?: DispoUser | RequesterUser;
  allCustomers: Customer[] = [];
  customers: Customer[] = [];
  isSwitching = false;

  customerInput = new FormControl('');

  customerInputValue = formControlValue$(this.customerInput);

  private destroyed$ = new Subject<void>();

  allCustomers$ = this.userService.readyUser$.pipe(
    pipe(takeUntil(this.destroyed$)),
    switchMap((u) => {
      if (u.role === RoleEnum.DISPO && u.isAdmin) {
        return this.subscriptionService.adminCustomers.ready$.pipe(
          map((ready) => ({})),
        );
      } else {
        return this.subscriptionService.myCustomer.ready$.pipe(
          filter((ready) => ready),
          map(() => {
            if (u.role === RoleEnum.REQUESTER) {
              return { requesterIds: u._id };
            }
            return { dispoIds: u._id };
          }),
        );
      }
    }),
    switchMap((query) => {
      return Customers.find(query);
    }),
    debounceTime(200),
    rxNgZoneRun(this.ngZone),
  );

  customerOptions$ = combineLatest([
    this.allCustomers$,
    this.customerInputValue,
  ]).pipe(
    map(([allCustomers, customerInputValue]) => {
      return allCustomers
        .map((customer) => ({
          customer,
          formattedName: this.formatCustomerName(customer),
        }))
        .filter((option) => {
          return (
            !customerInputValue ||
            stringIncludes(option.formattedName, customerInputValue || '')
          );
        });
    }),
  );

  filteredCustomers$ = this.customerOptions$.pipe(
    map((options) => options.map((option) => option.customer)),
  );

  subscribeCustomersEffect$ = this.allCustomers$.pipe(
    tap((c) => {
      this.allCustomers = c;
    }),
    takeUntil(this.destroyed$),
  );

  currentCustomerName$ = combineLatest([
    this.allCustomers$.pipe(rxFilterArrayIsNotEmpty()),
    this.userService.readyUser$,
  ]).pipe(
    takeUntil(this.destroyed$),
    map(([allCustomers, user]) => {
      if (user.role !== RoleEnum.DISPO && user.role !== RoleEnum.REQUESTER) {
        return undefined;
      }
      const customer = allCustomers.find(
        (customer) => customer._id === user.customerId,
      );
      return customer ? this.formatCustomerName(customer) : undefined;
    }),
  );

  /**
   * Format customer name with demo indicator
   * @param customer - The customer to format
   * @returns Formatted customer name with "(Demo)" or "(Demo X)" if applicable
   */
  formatCustomerName(customer: Customer): string {
    if (!customer.isDemo) {
      return customer.name || '';
    }
    if (customer.demoInstanceNumber) {
      return `${customer.name} (Demo ${customer.demoInstanceNumber})`;
    }
    return `${customer.name} (Demo)`;
  }

  initCustomerInputValueEffect$ = this.currentCustomerName$.pipe(
    take(1),
    tap((currentCustomerName) => {
      this.customerInput.setValue(currentCustomerName);
    }),
    takeUntil(this.destroyed$),
  );

  constructor(
    private toastService: ToastService,
    private ngZone: NgZone,
    private userService: UserService,
    private subscriptionService: SubscriptionService,
  ) {
    this.subscribeCustomersEffect$.subscribe();
    this.initCustomerInputValueEffect$.subscribe();
  }

  onFocus() {
    this.customerInput.setValue('');
  }

  matInputSelect(event: MatAutocompleteSelectedEvent) {
    this.selectCustomer(event.option.value);
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  clearCustomerInput(event: Event) {
    event.stopPropagation();
    this.selectCustomer(undefined);
    // @ts-ignore
    this.customerInput.control.nativeElement.blur();
    // @ts-ignore
    this.customerInput.control.nativeElement.focus();
  }

  selectCustomer(customerId?: string): void {
    if (!customerId) {
      this.customerInput.setValue('');
    } else {
      this.isSwitching = true;
      Meteor.call('updateCustomerId', customerId, (error: Meteor.Error) => {
        if (error) {
          this.isSwitching = false;
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          location.reload();
        }
      });
    }
  }
}
